import { ApiError } from "./api.js";

/** Look up a required element by id (throws if the markup/id drifted). */
export function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

/** Look up an element that the page can do without. Returns null when it isn't
 *  there, so a caller can skip one feature instead of taking the page down with
 *  it. Use for anything additive; keep `el` for the markup a page IS. Assets are
 *  cached independently, so a browser can pair a stale document with a fresh
 *  bundle - and one absent node should not cost a settings page its members,
 *  keys and save button. */
export function elOptional<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

// Locale-aware date+time, using the visitor's browser locale (the `undefined`
// locale). Created once and reused. Native Intl - no date library needed.
const DATE_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
  hour12: false, // 24-hour clock regardless of the locale's default
});

/** Format an ISO timestamp as a locale-aware date + time. */
export function formatDateTime(iso: string): string {
  return DATE_TIME_FMT.format(new Date(iso));
}

/** Append a `.perm-check` label-wrapped input to `container`, returning the
 *  input. Shared by radioGroup/checkboxGroup. */
function appendCheckable(
  container: HTMLElement,
  opts: { type: "radio" | "checkbox"; name?: string; value: string; label: string; checked: boolean; onChange?: () => void },
): HTMLInputElement {
  const label = document.createElement("label");
  label.className = "perm-check";
  const input = document.createElement("input");
  input.type = opts.type;
  if (opts.name) input.name = opts.name;
  input.value = opts.value;
  input.checked = opts.checked;
  if (opts.onChange) input.addEventListener("change", opts.onChange);
  label.append(input, document.createTextNode(" " + opts.label));
  container.append(label);
  return input;
}

let radioSeq = 0;

/** Build a single-select radio group into `container` (cleared first). Each radio
 *  shares a unique generated `name` so groups never interfere. Returns a getter
 *  for the currently-selected value ("" when nothing is checked). */
export function radioGroup(
  container: HTMLElement,
  items: { value: string; label: string }[],
  selected: string,
  onChange?: (value: string) => void,
): () => string {
  const name = `rg-${radioSeq++}`;
  container.replaceChildren();
  // A radio fires `change` only when it becomes checked, so the value is `it.value`.
  const inputs = items.map((it) =>
    appendCheckable(container, {
      type: "radio",
      name,
      value: it.value,
      label: it.label,
      checked: it.value === selected,
      onChange: onChange ? () => onChange(it.value) : undefined,
    }),
  );
  return () => inputs.find((i) => i.checked)?.value ?? "";
}

/** Build a multi-select checkbox group into `container`; returns the inputs. */
export function checkboxGroup(
  container: HTMLElement,
  items: { key: string; label: string }[],
  isChecked: (key: string) => boolean,
  onChange?: () => void,
): HTMLInputElement[] {
  return items.map((it) =>
    appendCheckable(container, {
      type: "checkbox",
      value: it.key,
      label: it.label,
      checked: isChecked(it.key),
      onChange,
    }),
  );
}

/** A trash-icon button that confirms, disables itself, runs `action`, and on
 *  success removes `row` from the DOM. On failure it re-enables and calls
 *  `onError` (if given). `label` is the title + aria-label. */
export function deleteButton(opts: {
  label: string;
  confirm: string;
  row: HTMLElement;
  action: () => Promise<unknown>;
  onError?: (err: unknown) => void;
}): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "session-delete";
  btn.title = opts.label;
  btn.setAttribute("aria-label", opts.label);
  btn.innerHTML = '<i class="fa-solid fa-trash-can" aria-hidden="true"></i>';
  btn.addEventListener("click", async () => {
    if (!confirm(opts.confirm)) return;
    btn.disabled = true;
    try {
      await opts.action();
      opts.row.remove();
    } catch (err) {
      btn.disabled = false;
      opts.onError?.(err);
    }
  });
  return btn;
}

/** Highest-privilege tier present in an audience set, given the order list
 *  (low→high). "" when the set is empty. Used to collapse a stored audience[]
 *  to the single clearance the (now single-select) UI shows. */
export function topTier(audiences: string[], order: readonly string[]): string {
  let best = "";
  let bestIdx = -1;
  for (const a of audiences) {
    const i = order.indexOf(a);
    if (i > bestIdx) {
      bestIdx = i;
      best = a;
    }
  }
  return best;
}

/** Copy `text`, then flash `done` on the button and restore its label. Falls back
 *  to a prompt when the clipboard is unavailable (an insecure context, e.g. a
 *  plain-http deployment), so the value is always obtainable by hand. */
export function copyOnClick(btn: HTMLButtonElement, text: () => string, done = "Copied"): void {
  btn.addEventListener("click", async () => {
    const value = text();
    if (!value) return;
    const label = btn.textContent;
    try {
      await navigator.clipboard.writeText(value);
      btn.textContent = done;
      setTimeout(() => (btn.textContent = label), 1500);
    } catch {
      prompt("Copy this value:", value);
    }
  });
}

export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.status ? `Error ${err.status}: ${err.message}` : err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
